<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { call } from '../api/http'
import { ledger } from '../api/ledger'
import { addMonths, budgetEdit, copyBudget, getBudget, plannedPreview, putBudget, thisMonth, toMinor, type Budget, type BudgetEdit } from '../api/planning'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import { formatMoney } from '../lib/money'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiBadge from '../components/UiBadge.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { locale } = useI18n()
const session = useSession()
const load = useForm()
const save = useForm()

const month = ref(thisMonth())
const budget = ref<Budget | null>(null)
const categories = ref<string[]>([])
const editing = ref(false)
const blank = (): BudgetEdit => ({ currency: 'EUR', income: [{ label: '', amount: '' }], limits: [{ category: '', mode: 'pct', value: '' }], note: '' })
const edit = ref<BudgetEdit>(blank())

watch(
  [() => session.workspace?.id, month],
  () =>
    load.submit(async () => {
      editing.value = false
      budget.value = null
      budget.value = await getBudget(month.value)
      edit.value = budget.value ? budgetEdit(budget.value) : blank()
    }),
  { immediate: true },
)
watch(
  () => session.workspace?.id,
  async () => (categories.value = (await call(ledger.GET('/categories')).catch(() => [])).map((c) => c.code)),
  { immediate: true },
)

const cur = computed(() => (/^[a-z]{3}$/i.test(edit.value.currency) ? edit.value.currency.toUpperCase() : 'EUR'))
const m = (amount_minor: number) => formatMoney({ amount_minor, currency: cur.value }, locale.value)
const fmt = (x: { amount_minor: number; currency: string }) => formatMoney(x, locale.value)
const barPct = (actual: number, planned: number) => (planned > 0 ? Math.min(100, (actual / planned) * 100) : actual > 0 ? 100 : 0)
const statusVariant = { ok: 'success', warn: 'secondary', over: 'danger' } as const

const incomePreview = computed(() => edit.value.income.reduce((s, r) => s + (toMinor(r.amount, cur.value) || 0), 0))
function cancelEdit() {
  if (budget.value) edit.value = budgetEdit(budget.value)
  editing.value = false
}

async function onSave() {
  const ok = await save.submit(async () => (budget.value = await putBudget(month.value, edit.value)))
  if (ok && budget.value) {
    edit.value = budgetEdit(budget.value)
    editing.value = false
  }
}
const onCopy = () => save.submit(async () => {
  budget.value = await copyBudget(month.value, addMonths(month.value, -1))
  edit.value = budgetEdit(budget.value)
})
const startNew = () => {
  edit.value = blank()
  editing.value = true
}
</script>

<template>
  <div class="page wide">
    <header class="page-header">
      <div>
        <h1>{{ $t('budget.title') }}</h1>
        <p class="muted">{{ $t('budget.subtitle') }}</p>
      </div>
      <div class="row">
        <UiButton variant="outline" size="sm" :aria-label="$t('budget.prev')" data-testid="budget-prev" @click="month = addMonths(month, -1)">←</UiButton>
        <input v-model="month" type="month" class="month" required :aria-label="$t('budget.month')" data-testid="budget-month" />
        <UiButton variant="outline" size="sm" :aria-label="$t('budget.next')" data-testid="budget-next" @click="month = addMonths(month, 1)">→</UiButton>
      </div>
    </header>

    <ErrorBanner :error="save.error.value ?? load.error.value" />
    <p v-if="load.busy.value" class="muted">{{ $t('common.loading') }}</p>

    <EmptyState v-else-if="!budget && !editing" :title="$t('budget.empty')" :message="$t('budget.emptyText')">
      <div v-if="session.canWrite" class="row" style="justify-content: center">
        <UiButton data-testid="budget-new" @click="startNew">{{ $t('budget.new') }}</UiButton>
        <UiButton variant="outline" :loading="save.busy.value" data-testid="budget-copy" @click="onCopy">{{ $t('budget.copyPrev') }}</UiButton>
      </div>
    </EmptyState>

    <template v-if="budget && !editing && !load.busy.value">
      <div class="grid-2" data-testid="budget-totals">
        <UiCard>
          <p class="muted small">{{ $t('budget.income') }}</p>
          <p class="big">{{ fmt(budget.income_total) }}</p>
          <p class="small muted">{{ $t('budget.incomeActual') }}: {{ fmt(budget.income_actual) }}</p>
        </UiCard>
        <UiCard>
          <p class="muted small">{{ $t('budget.plannedVsActual') }}</p>
          <p class="big">{{ fmt(budget.actual_total) }} / {{ fmt(budget.planned_total) }}</p>
          <p class="small muted">{{ $t('budget.unbudgeted') }}: <span data-testid="budget-unbudgeted">{{ fmt(budget.unbudgeted_actual) }}</span></p>
        </UiCard>
        <UiCard>
          <p class="muted small">{{ $t('budget.net') }}</p>
          <p :class="['big', budget.net.amount_minor < 0 ? 'out' : 'in']">{{ formatMoney(budget.net, locale, true) }}</p>
        </UiCard>
      </div>

      <UiCard>
        <div class="lines" data-testid="budget-lines">
          <div v-for="l in budget.lines" :key="l.category" class="line" data-testid="budget-line">
            <div class="line-head">
              <strong>{{ l.category }}</strong>
              <span v-if="l.pct" class="muted small">{{ l.pct }}%</span>
              <UiBadge :variant="statusVariant[l.status]" :class="l.status">{{ $t(`budget.status.${l.status}`) }}</UiBadge>
              <span class="spacer" />
              <span class="num"><span data-testid="line-actual">{{ fmt(l.actual) }}</span> / <span data-testid="line-planned">{{ fmt(l.planned) }}</span></span>
            </div>
            <div class="bar" role="progressbar" :aria-valuenow="Math.round(barPct(l.actual.amount_minor, l.planned.amount_minor))" aria-valuemin="0" aria-valuemax="100" :aria-label="l.category">
              <div :class="['fill', l.status]" :style="{ width: `${barPct(l.actual.amount_minor, l.planned.amount_minor)}%` }" />
            </div>
            <p class="small muted">{{ $t('budget.left') }}: {{ fmt(l.diff) }}</p>
          </div>
          <p v-if="!budget.lines.length" class="muted small">—</p>
        </div>
        <p v-if="budget.note" class="small">{{ budget.note }}</p>
        <div v-if="session.canWrite" class="row">
          <UiButton variant="outline" data-testid="budget-edit" @click="editing = true">{{ $t('common.edit') }}</UiButton>
        </div>
      </UiCard>
    </template>

    <UiCard v-if="editing && session.canWrite" :title="$t('budget.edit')">
      <form class="stack" data-testid="budget-form" @submit.prevent="onSave">
        <label class="inline">
          <span class="small">{{ $t('accounts.currency') }}</span>
          <input v-model.trim="edit.currency" name="currency" class="cur" required pattern="[A-Za-z]{3}" maxlength="3" />
        </label>

        <h3>{{ $t('budget.incomeRows') }}</h3>
        <div v-for="(r, i) in edit.income" :key="`i${i}`" class="row" data-testid="income-row">
          <input v-model="r.label" name="income_label" class="grow" maxlength="100" :placeholder="$t('budget.label')" :aria-label="$t('budget.label')" />
          <input v-model.trim="r.amount" name="income_amount" class="amt" inputmode="decimal" pattern="\d+([.,]\d{1,2})?" :placeholder="$t('budget.amount')" :aria-label="$t('budget.amount')" />
          <UiButton variant="ghost" size="sm" :aria-label="$t('common.delete')" @click="edit.income.splice(i, 1)">×</UiButton>
        </div>
        <div><UiButton variant="outline" size="sm" data-testid="add-income" @click="edit.income.push({ label: '', amount: '' })">+ {{ $t('budget.addIncome') }}</UiButton></div>

        <h3>{{ $t('budget.limits') }}</h3>
        <datalist id="budget-cats"><option v-for="c in categories" :key="c" :value="c" /></datalist>
        <div v-for="(l, i) in edit.limits" :key="`l${i}`" class="row" data-testid="limit-row">
          <input v-model="l.category" name="limit_category" class="cat" list="budget-cats" maxlength="32" :placeholder="$t('transactions.category')" :aria-label="$t('transactions.category')" />
          <select v-model="l.mode" name="limit_mode" class="mode" :aria-label="$t('budget.mode')">
            <option value="pct">{{ $t('budget.modePct') }}</option>
            <option value="amount">{{ $t('budget.modeAmount') }}</option>
          </select>
          <input v-model.trim="l.value" name="limit_value" class="amt" inputmode="decimal" required
            :pattern="l.mode === 'pct' ? '\\d{1,3}([.,]\\d{1,4})?' : '\\d+([.,]\\d{1,2})?'" :aria-label="$t('budget.amount')" />
          <span class="muted small num" data-testid="limit-preview">= {{ Number.isNaN(plannedPreview(edit, l)) ? '—' : m(plannedPreview(edit, l)) }}</span>
          <UiButton variant="ghost" size="sm" :aria-label="$t('common.delete')" @click="edit.limits.splice(i, 1)">×</UiButton>
        </div>
        <div><UiButton variant="outline" size="sm" data-testid="add-limit" @click="edit.limits.push({ category: '', mode: 'pct', value: '' })">+ {{ $t('budget.addLimit') }}</UiButton></div>
        <p class="small muted">{{ $t('budget.income') }}: {{ m(incomePreview) }}</p>

        <input v-model="edit.note" name="note" maxlength="1000" :placeholder="$t('bills.note')" :aria-label="$t('bills.note')" />
        <div class="row">
          <UiButton type="submit" :loading="save.busy.value" data-testid="budget-save">{{ $t('common.save') }}</UiButton>
          <UiButton variant="ghost" @click="cancelEdit">{{ $t('common.cancel') }}</UiButton>
        </div>
      </form>
    </UiCard>
  </div>
</template>

<style scoped>
.wide { max-width: 1100px; }
.month { width: auto; }
.big { font-size: 1.4rem; font-weight: 600; font-variant-numeric: tabular-nums; }
.in { color: var(--gain); }
.out { color: var(--loss); }
.num { font-variant-numeric: tabular-nums; }
.lines { display: flex; flex-direction: column; gap: 1rem; }
.line-head { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.bar { height: 10px; border-radius: 999px; background: var(--border); overflow: hidden; margin-top: 0.3rem; }
.fill { height: 100%; border-radius: 999px; }
.fill.ok { background: var(--gain); }
.fill.warn { background: var(--warn); }
.fill.over { background: var(--loss); }
.badge.warn { background: var(--warn); color: #fff; }
.grow { flex: 1; min-width: 140px; }
.amt { width: 8rem; }
.cat { width: 9rem; }
.mode, .cur { width: auto; }
.inline { display: flex; align-items: center; gap: 0.5rem; }
</style>
